import { Grid } from "@mui/material";
import CustomFormField from "components/CustomFormField";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { connect } from "react-redux";

const UserForm = ({
  formData,
  localizations,
  allOrganizations,
  subOrganizations,
  classes,
  isEditMode,
  onFetchRequest,
}) => {
  const {
    formField,
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = formData;

  const {
    organization: { errorMsg: _organization, ...organizationRest },
    subOrganization: { errorMsg: _subOrganization, ...subOrganizationRest },
    classData: { errorMsg: _classData, ...classDataRest },
    email: { errorMsg: _email, ...emailRest },
    userType: { errorMsg: _userType, ...userTypeRest },
    name: { errorMsg: _name, ...nameRest },
    surname: { errorMsg: _surname, ...surnameRest },
    nickName: { errorMsg: _nickName, ...nickNameRest },
    studentNo: { errorMsg: _studentNo, ...studentNoRest },
    allBooksEnabled: { errorMsg: _allBooksEnabled, ...allBooksEnabledRest },
  } = formField;

  const {
    organization: organizationV,
    subOrganization: subOrganizationV,
    classData: classDataV,
    email: emailV,
    userType: userTypeV,
    name: nameV,
    surname: surnameV,
    nickName: nickNameV,
    studentNo: studentNoV,
    allBooksEnabled: allBooksEnabledV,
  } = values;

  const isUserTypeTeacher = userTypeV?.value === "OTHER" || userTypeV?.value === "TEACHER";

  useEffect(() => {
    const orgId = organizationV?.value || organizationV;

    if (orgId && !isEditMode) {
      onFetchRequest("ORG_CHANGED", orgId, null);
    }
  }, [organizationV]);

  useEffect(() => {
    const orgId = organizationV?.value || organizationV;
    const subOrgId = subOrganizationV?.value || subOrganizationV;

    if (orgId && subOrgId && !isEditMode) {
      onFetchRequest("SUB_ORG_CHANGED", orgId, subOrgId);
    }
  }, [subOrganizationV]);

  return (
    <>
      <SuiTypography variant="h5">
        {localizations?.USER_FORM_HEADER ?? "User Details"}
      </SuiTypography>
      <SuiBox mt={1} mb={5}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={4}>
            <CustomFormField
              {...userTypeRest}
              options={[
                { value: "STUDENT", label: "ÖĞRENCİ" },
                { value: "TEACHER", label: "ÖĞRETMEN" },
              ]}
              value={userTypeV}
              error={errors.userType?.length > 0 && touched.userType}
              success={userTypeV?.length > 0 && !errors.userType}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              isDisabled={isEditMode}
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <SuiBox
              sx={{
                cursor: isEditMode ? "not-allowed" : "default",
              }}
            >
              <CustomFormField
                {...organizationRest}
                options={
                  Array.isArray(allOrganizations) && allOrganizations?.length > 0
                    ? allOrganizations.map((organization) => ({
                        value: organization.id,
                        label: organization.organizationKey,
                      }))
                    : []
                }
                resets={[
                  { name: "subOrganization", value: null },
                  { name: "classData", value: null },
                ]}
                value={organizationV}
                error={errors.organization?.length > 0 && !(touched.organization !== true)}
                success={organizationV?.length > 0 && !errors.organization}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                isDisabled={isEditMode}
              />
            </SuiBox>
          </Grid>
          <Grid item xs={12} lg={4}>
            <SuiBox
              sx={{
                cursor: isEditMode ? "not-allowed" : "default",
              }}
            >
              <CustomFormField
                {...subOrganizationRest}
                options={
                  Array.isArray(subOrganizations) && subOrganizations?.length > 0
                    ? subOrganizations.map((subOrganization) => ({
                        value: subOrganization.id,
                        label: subOrganization.subOrganizationKey,
                      }))
                    : []
                }
                resets={[{ name: "classData", value: null }]}
                value={subOrganizationV}
                error={errors.subOrganization?.length > 0 && !(touched.subOrganization !== true)}
                success={subOrganizationV?.length > 0 && !errors.subOrganization}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                isDisabled={isEditMode || !organizationV}
              />
            </SuiBox>
          </Grid>
          <Grid item xs={12} lg={4}>
            <CustomFormField
              {...classDataRest}
              isMulti={isUserTypeTeacher}
              options={
                Array.isArray(classes) && classes?.length > 0
                  ? classes.map((c) => ({
                      value: c.id,
                      label: c.className,
                    }))
                  : []
              }
              value={classDataV}
              error={errors.classData?.length > 0 && !(touched.classData !== true)}
              success={classDataV?.length > 0 && !errors.classData}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              isDisabled={!subOrganizationV}
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <CustomFormField
              {...emailRest}
              value={emailV}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.email?.length > 0 && touched.email}
              success={emailV?.length > 0 && !errors.email}
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <CustomFormField
              {...nameRest}
              value={nameV}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.name?.length > 0 && touched.name}
              success={nameV?.length > 0 && !errors.name}
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <CustomFormField
              {...surnameRest}
              value={surnameV}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.surname?.length > 0 && touched.surname}
              success={surnameV?.length > 0 && !errors.surname}
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <CustomFormField
              {...nickNameRest}
              value={nickNameV}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.nickName?.length > 0 && touched.nickName}
              success={nickNameV?.length > 0 && !errors.nickName}
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <CustomFormField
              {...studentNoRest}
              value={studentNoV ?? ""}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.studentNo?.length > 0 && touched.studentNo}
              success={studentNoV?.length > 0 && !errors.studentNo}
              disabled={isUserTypeTeacher}
            />
          </Grid>

          {isEditMode && (
            <Grid item xs={12} lg={4}>
              <CustomFormField
                {...allBooksEnabledRest}
                type="switch"
                value={allBooksEnabledV}
                label={localizations?.ALL_BOOKS_ENABLED ?? "Tüm Kitaplara Erişim"}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
              />
            </Grid>
          )}
        </Grid>
      </SuiBox>
    </>
  );
};

UserForm.defaultProps = {
  localizations: null,
  allOrganizations: null,
  subOrganizations: null,
  classes: null,
  isEditMode: false,
  onFetchRequest: () => {},
};

UserForm.propTypes = {
  formData: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
  localizations: PropTypes.objectOf(PropTypes.any),
  allOrganizations: PropTypes.arrayOf(PropTypes.any),
  subOrganizations: PropTypes.arrayOf(PropTypes.any),
  classes: PropTypes.arrayOf(PropTypes.any),
  isEditMode: PropTypes.bool,
  onFetchRequest: PropTypes.func,
};

const mapStateToProps = (state) => ({
  localizations: state.localization?.pairs,
});

export default connect(mapStateToProps, null)(UserForm);
